/**
 * Quantity maths for the printed job card.  One copy, used by the print page
 * and the PDF download, so the two can never disagree.
 */

/**
 * A stored quantity as a number.  Takes numbers and text such as "5,500",
 * " 2 " or "2 UPS"; 0 when it is absent, unreadable or not positive.
 */
export function qtyNum(v: unknown): number {
  if (v == null) return 0;
  if (typeof v === 'number') return Number.isFinite(v) && v > 0 ? v : 0;
  const s = String(v).replace(/,/g, '').trim();
  if (!s) return 0;
  const n = Number(s);
  if (Number.isFinite(n)) return n > 0 ? n : 0;
  const m = /\d+(?:\.\d+)?/.exec(s);
  return m ? Number(m[0]) : 0;
}

/**
 * Actual Sheet and Wastage, exactly as the job card form makes them:
 *
 *   Actual Sheet = Client Required Qty ÷ UPS, rounded up
 *   Wastage      = Sheet (incl. W) − Actual Sheet
 *
 * Only Sheet (incl. W) is stored, so both are worked out from the card's own
 * Client Required Qty and UPS whenever it has them — never guessed from the PO
 * or the box count, and never capped at the stored sheets.  Wastage is not
 * printed below 0: if a card's sheets fall short of its actual, the two
 * numbers show it.
 *
 * Cards saved without a Client Required Qty (old data) keep the earlier
 * fallback: the PO qty when it fits the sheets, else the box count.
 */
export function splitSheets(
  clientQty: unknown,
  ups: unknown,
  totalWithWastage: unknown,
  totalBoxes?: unknown,
  poQty?: unknown,
): { base: string; wastage: string } {
  const cq = qtyNum(clientQty);
  const u = qtyNum(ups);
  const total = qtyNum(totalWithWastage);

  if (cq > 0 && u > 0) {
    // Same expression as the form's calculateBaseSheets().
    const base = Math.ceil(cq / Math.max(1, u));
    return { base: String(base), wastage: total > 0 ? String(Math.max(0, total - base)) : '' };
  }

  if (total <= 0) return { base: '', wastage: '' };
  const pq = qtyNum(poQty);
  const tb = qtyNum(totalBoxes);
  let base = 0;
  if (u > 0) {
    if (pq > 0 && Math.ceil(pq / u) <= total) base = Math.ceil(pq / u);
    else if (tb > 0) base = Math.min(total, Math.ceil(tb / u));
  }
  if (base <= 0 || base > total) return { base: String(total), wastage: '0' };
  return { base: String(base), wastage: String(total - base) };
}

/**
 * The Client Required Qty to print above Total Print Box, or null.  It is
 * shown only when the job is for less than the PO — otherwise it would just
 * repeat the PO Qty.
 */
/**
 * The client's quantity line on the printed job card, whenever it differs
 * from the PO qty: over the PO it is the "Qty with Tolerance", under it the
 * "Client Required Qty".  Null when equal or either is missing.
 */
export function clientQtyLine(clientQty: unknown, poQty: unknown): { label: string; value: string } | null {
  const cq = qtyNum(clientQty);
  const pq = qtyNum(poQty);
  if (!(cq > 0) || !(pq > 0) || cq === pq) return null;
  return { label: cq > pq ? 'Qty with Tolerance' : 'Client Required Qty', value: cq.toLocaleString('en-IN') };
}

export function clientRequiredQty(clientQty: unknown, poQty: unknown): number | null {
  const cq = qtyNum(clientQty);
  const pq = qtyNum(poQty);
  return cq > 0 && pq > 0 && cq < pq ? cq : null;
}
